"use client";

import { ImageType, ProjectType } from "@prisma/client";
import { useEffect, useState } from "react";
import { Description, Title } from "../components";
import { frameCostPerClothChange } from "@/app/(signed-in)/create/ai-clothes-changer/constants";
import { frameCostPerFaceEdit } from "@/app/(signed-in)/create/ai-face-editor/constants";
import { frameCostPerHeadshot } from "@/app/(signed-in)/create/ai-headshot-generator/constants";
import { frameCostPerImage } from "@/app/(signed-in)/create/ai-image-generator/constants";
import { frameCostPerImageUpscale } from "@/app/(signed-in)/create/ai-image-upscaler/constants";
import { frameCostPerMeme } from "@/app/(signed-in)/create/ai-meme-generator/constants";
import { frameCostPerEditedPhoto } from "@/app/(signed-in)/create/ai-photo-editor/constants";
import { frameCostPerQrCode } from "@/app/(signed-in)/create/ai-qr-code-generator/constants";
import { frameCostPerFaceSwapImage } from "@/app/(signed-in)/create/face-swap-photo/constants";
import { frameCostPerImageBackgroundRemoval } from "@/app/(signed-in)/create/image-background-remover/constants";
import { frameCostPerPhotoColorization } from "@/app/(signed-in)/create/photo-colorizer/constants";
import { costMultiplierByType } from "@/shared/constants";
import { InfoDrawer } from "@/shared/info-drawer";
import { usagePricingByTier } from "@/shared/usage-pricing";

type Unit = "second" | "count";
type Mode = Exclude<ProjectType | ImageType, "FACE_SWAP"> | "FACE_SWAP_VIDEO" | "FACE_SWAP_PHOTO";
type InputConfig = {
  [key in Mode]: {
    unit: Unit;
    costPerImage: number;
  };
};

const fps = 24;

const inputConfig: InputConfig = {
  ANIMATION: {
    unit: "second",
    costPerImage: costMultiplierByType.ANIMATION,
  },
  AUTO_SUBTITLE: {
    unit: "second",
    costPerImage: costMultiplierByType.AUTO_SUBTITLE,
  },
  IMAGE_TO_VIDEO: {
    unit: "second",
    costPerImage: costMultiplierByType.IMAGE_TO_VIDEO,
  },
  VIDEO_TO_VIDEO: {
    unit: "second",
    costPerImage: costMultiplierByType.VIDEO_TO_VIDEO,
  },
  TEXT_TO_VIDEO: {
    unit: "second",
    costPerImage: costMultiplierByType.TEXT_TO_VIDEO,
  },
  TALKING_PHOTO: {
    unit: "second",
    costPerImage: costMultiplierByType.TALKING_PHOTO,
  },
  FACE_SWAP_VIDEO: {
    unit: "second",
    costPerImage: costMultiplierByType.FACE_SWAP,
  },
  LIP_SYNC: {
    unit: "second",
    costPerImage: costMultiplierByType.LIP_SYNC,
  },
  FACE_SWAP_PHOTO: {
    unit: "count",
    costPerImage: frameCostPerFaceSwapImage,
  },
  FACE_EDITOR: {
    unit: "count",
    costPerImage: frameCostPerFaceEdit,
  },
  AI_HEADSHOT: {
    unit: "count",
    costPerImage: frameCostPerHeadshot,
  },
  AI_IMAGE: {
    unit: "count",
    costPerImage: frameCostPerImage,
  },
  AI_MEME: {
    unit: "count",
    costPerImage: frameCostPerMeme,
  },
  IMAGE_UPSCALER: {
    unit: "count",
    costPerImage: frameCostPerImageUpscale,
  },
  PHOTO_EDITOR: {
    unit: "count",
    costPerImage: frameCostPerEditedPhoto,
  },
  QR_CODE: {
    unit: "count",
    costPerImage: frameCostPerQrCode,
  },
  BACKGROUND_REMOVER: {
    unit: "count",
    costPerImage: frameCostPerImageBackgroundRemoval,
  },
  CLOTHES_CHANGER: {
    unit: "count",
    costPerImage: frameCostPerClothChange,
  },
  PHOTO_COLORIZER: {
    unit: "count",
    costPerImage: frameCostPerPhotoColorization,
  },
};

const resolutionsByTier = ["starter", "creator", "pro", "business"] as const;

type Tier = (typeof resolutionsByTier)[number];

function costPer1K(tier: keyof typeof usagePricingByTier) {
  return (parseFloat(usagePricingByTier[tier][0].unit_amount_decimal) * 10).toFixed(2);
}

export function CalculateCost() {
  const [mode, setMode] = useState<Mode>("FACE_SWAP_VIDEO");
  const [tier, setTier] = useState<Tier>("creator");
  const [unit, setUnit] = useState<Unit>("second");
  const [value, setValue] = useState<number>(60);

  useEffect(() => {
    setUnit(inputConfig[mode].unit);
  }, [mode]);

  // Calculate cost based on inputs
  const calculateCost = () => {
    const config = inputConfig[mode];
    const frames = value * config.costPerImage * (config.unit === "second" ? fps : 1);

    const prices = usagePricingByTier[tier];

    let totalCost = 0;
    let remainingFrames = frames;

    for (const priceTier of prices) {
      const { up_to, unit_amount_decimal } = priceTier;
      const limit = up_to === "inf" ? Infinity : up_to;
      const applicableFrames = Math.min(remainingFrames, limit);

      totalCost += (applicableFrames * parseFloat(unit_amount_decimal)) / 100;
      remainingFrames -= applicableFrames;

      if (remainingFrames <= 0) break;
    }

    const decimal = totalCost > 1 ? (totalCost > 100 ? 0 : 2) : 4;

    return totalCost.toFixed(decimal);
  };

  return (
    <div className="flex w-full max-w-full flex-col items-center px-2" id="api-cost-calculator">
      <div className="flex flex-col items-center gap-8">
        <Title>Estimate Your API Costs</Title>

        <Description>
          Simple transparent pricing based on your usage. Only pay for what you need and get volume
          discounts as you scale.
        </Description>

        <div className="flex w-full flex-col justify-center gap-4 rounded-3xl border border-slate-600 bg-slate-900 p-6">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Mode</legend>
            <select
              className="select w-full border bg-transparent"
              value={mode}
              onChange={(e) => setMode(e.target.value as Mode)}
            >
              {/* TODO: automatically generate these */}
              <option value="CLOTHES_CHANGER">AI Clothes Changer</option>
              <option value="AI_HEADSHOT">AI Headshot Generator</option>
              <option value="AI_IMAGE">AI Image Generator</option>
              <option value="IMAGE_UPSCALER">AI Image Upscaler</option>
              <option value="QR_CODE">AI QR Code Generator</option>
              <option value="ANIMATION">Animation</option>
              <option value="AUTO_SUBTITLE">Auto Subtitle Generator</option>
              <option value="FACE_SWAP_PHOTO">Face Swap Photo</option>
              <option value="FACE_SWAP_VIDEO">Face Swap Video</option>
              <option value="BACKGROUND_REMOVER">Image Background Remover</option>
              <option value="IMAGE_TO_VIDEO">Image-to-Video</option>
              <option value="LIP_SYNC">Lip Sync</option>
              <option value="TEXT_TO_VIDEO">Text-to-Video</option>
              <option value="VIDEO_TO_VIDEO">Video-to-Video</option>
            </select>
          </fieldset>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                <InfoDrawer
                  drawerId={"api-tier"}
                  label={"Tier"}
                  content={
                    <div className="flex flex-col gap-0">
                      <div>
                        <div className="flex w-full justify-between">
                          <div className="font-bold">Starter Tier</div>
                          <div>${costPer1K("starter")} / 1000 frames</div>
                        </div>
                        <ul className="m-0 text-sm">
                          <li className="my-0">512x512 resolution</li>
                          <li className="my-0">100MB uploads</li>
                          <li className="my-0">
                            No access to Image-to-Video, Text-to-Video, and Video-to-Video
                          </li>
                        </ul>
                      </div>

                      <div>
                        <div className="flex w-full justify-between">
                          <div className="font-bold">Creator Tier</div>
                          <div>${costPer1K("creator")} / 1000 frames</div>
                        </div>
                        <ul className="m-0 text-sm">
                          <li className="my-0">1024x1024 resolution</li>
                          <li className="my-0">1GB uploads</li>
                        </ul>
                      </div>
                      <div>
                        <div className="flex w-full justify-between">
                          <div className="font-bold">Pro Tier</div>
                          <div>${costPer1K("pro")} / 1000 frames</div>
                        </div>
                        <ul className="m-0 text-sm">
                          <li className="my-0">1472x1472 resolution</li>
                          <li className="my-0">2GB uploads</li>
                        </ul>
                      </div>
                      <div>
                        <div className="flex w-full justify-between">
                          <div className="font-bold">Business Tier</div>
                          <div>${costPer1K("business")} / 1000 frames</div>
                        </div>
                        <ul className="m-0 text-sm">
                          <li className="my-0">4096x4086 resolution for select modes</li>
                          <li className="my-0">3GB uploads</li>
                        </ul>
                      </div>
                      <div className="rounded-lg bg-slate-800 p-2 text-sm">
                        All paid plans include: volume discounts (up to 50% off), no watermark,
                        priority compute, and dedicated support.
                      </div>
                      <div>
                        <div className="mt-6 overflow-x-auto">
                          <div>Volume Discounts</div>
                          <table className="table-xs m-0 mt-0 table rounded-lg border border-slate-800 p-1">
                            <thead>
                              <tr>
                                <th className="text-xs">Frame Usage Per Month</th>
                                <th className="text-xs break-words">
                                  Discount Applied to <br />
                                  Frames in this Range
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {usagePricingByTier.creator.map((price, idx) => {
                                const previousUpTo =
                                  usagePricingByTier.creator[idx - 1]?.up_to ?? "0";
                                return (
                                  <tr key={price.up_to}>
                                    <th className="text-xs">
                                      {previousUpTo.toLocaleString()}
                                      {price.up_to === "inf"
                                        ? "+"
                                        : ` - ${price.up_to.toLocaleString()}`}
                                    </th>
                                    <td>{idx * 10}%</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  }
                />
              </legend>
              <select
                className="select w-full border bg-transparent"
                value={tier}
                onChange={(e) => setTier(e.target.value as Tier)}
              >
                {resolutionsByTier.map((entry) => {
                  return (
                    <option key={entry} value={entry}>
                      {entry.charAt(0).toUpperCase() + entry.slice(1).toLowerCase()}
                    </option>
                  );
                })}
              </select>
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend h-10">
                {unit === "second" ? "Seconds of Video" : "Number of Images"}
              </legend>
              <input
                type="number w-full"
                value={value}
                onChange={(e) => {
                  const val = parseInt(e.target.value);

                  if (!isNaN(val)) {
                    setValue(Math.min(val, 1_000_000));
                  }
                }}
                className="input w-full border bg-transparent focus:outline-hidden"
              />
            </fieldset>
          </div>

          <div className="h-28">
            <div className="stat place-items-center">
              <div className="stat-title">Estimated monthly cost</div>
              <div className="stat-value">${calculateCost()}</div>

              <div className="stat-desc">{unit === "second" ? `Based on ${fps} FPS` : ""}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
